import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';

/**
 * FRONT-2669 — what Eddy's last edit to a document changed, for the Canvas
 * "Changes" view. `GET /api/artifacts/document/:id/last-change` answers
 * `{ available: false }` (no earlier version) or `{ available: true, before,
 * after, changedAt }`.
 */
export type DocumentLastChange =
  | { available: false }
  | {
      available: true;
      before: string;
      after: string;
      changedAt: string;
      /** True when the pair spans edits made AFTER Eddy's last one (a missing field reads as false). */
      laterEdits?: boolean;
    };

/**
 * The key folds in BOTH the document id and its current `updatedAt` (FRONT-1501:
 * TanStack refetches when the KEY changes). `updatedAt` is what makes the view
 * refetch after each edit, and a cached answer for the previous version can never
 * describe the current one. `documentLastChangesFor(id)` is the invalidation
 * prefix the `artifact-updated` handler uses.
 */
export const artifactChangeKeys = {
  all: ['artifact-changes'] as const,
  documentLastChanges: () => [...artifactChangeKeys.all, 'document-last-change'] as const,
  documentLastChangesFor: (id: string) => [...artifactChangeKeys.documentLastChanges(), id] as const,
  documentLastChange: (id: string, updatedAt: string | null | undefined) =>
    [...artifactChangeKeys.documentLastChangesFor(id), updatedAt ?? null] as const,
};

/** Enabled only once the document's current `updatedAt` is known, so the key is never half-built. */
export function useDocumentLastChange(id: string | null | undefined, updatedAt: string | null | undefined) {
  return useQuery({
    queryKey: artifactChangeKeys.documentLastChange(id ?? '', updatedAt),
    queryFn: () => api.get<DocumentLastChange>(`/api/artifacts/document/${encodeURIComponent(id!)}/last-change`),
    enabled: !!id && !!updatedAt,
    // A refusal here is a fixed answer (flag off, not yours): retrying only delays the message.
    retry: false,
  });
}
